/* eslint-disable camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import GroupAddIcon from '@material-ui/icons/GroupAdd';

import {
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  IconButton,
} from '@material-ui/core';
import { connect } from 'react-redux';
import {
  determineEmployeeType,
  formatEmployeeDisplayName
} from '../helpers/helpers';
import { modelPositions } from '../constants/employeePositions';
import i18n from '../i18n';
import EmployeeSearchTable from '../components/EmployeeSearchTable';
import AddEmployeeModal from '../components/Employee/AddEmployeeModal';
import RequireScope from '../components/RequireScope';
import '../assets/stylesheets/SearchView.css';
import { UserType } from '../validation';
import employeeTypeMenuItems from '../components/Employee/EmployeeTypeMenuItems';
import { fetchPositionsBasic, fetchStudioEmployeeBasic } from '../helpers/externalRequests';

class SearchView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filteredPosition: 'none',
      filteredEmployeeType: 'none',
      filteredEmployees: [],
      allPositions: [],
      showInactive: false,
      snackOpen: false,
      snackbarMessage: ''
    };
    this.employees = [];
    this.addEmployeeModalRef = React.createRef();
  }

  displaySnackbar = message => {
    this.setState({
      snackOpen: true,
      snackbarMessage: message
    });
  };

  hideSnackbar = () => {
    this.setState({ snackOpen: false });
  };

  filterEmployees = (showInactive, filteredPosition, filteredEmployeeType) => {
    let filteredEmployees = [];
    if (filteredPosition !== 'none') {
      filteredEmployees = showInactive
        ? this.employees.filter(employee =>
            employee.positions.includes(filteredPosition)
          )
        : this.employees.filter(
            employee =>
              employee.isActive && employee.positions.includes(filteredPosition) 
          );
    } else {
      filteredEmployees = showInactive
        ? this.employees
        : this.employees.filter(employee => employee.isActive === true);
    }
    if (filteredEmployeeType !== 'none') {
      filteredEmployees = filteredEmployees.filter(
        employee => employee.employeeType === filteredEmployeeType
      );
    }
    return filteredEmployees;
  };

  formatEmployees = rawEmployees => {
    return rawEmployees.map(employee => {
      const {
        profileImage,
        firstName,
        lastName,
        nickName,
        email,
        positions,
        origins,
        nationalities,
        id,
        primaryPosition,
        primaryPositionId,
        positions_studioemployee,
        isActive,
        isFullTime,
        isContractor
      } = employee;

      const name = formatEmployeeDisplayName(firstName, lastName, nickName);

      const employeeType = determineEmployeeType(isFullTime, isContractor);

      const employeeTypeDisplay = i18n.t(employeeType);

      let phoneNumber = i18n.t('noData');
      // If the employee has a main phone number, use that one.
      if (employee.phoneNumber) {
        if (employee.phoneNumber[employee.primaryPhoneNumberKey]) {
          phoneNumber = employee.phoneNumber[employee.primaryPhoneNumberKey];
        }
      }

      const formattedPositions = positions.map(
        position => position.positionName
      );
      const formattedOrigins = origins ? origins
        .map(origin => origin.originName)
        .join(', ') : '';
      const formattedNationalities = nationalities ? nationalities
        .map(nationality => i18n.t(`country:${nationality.nationalityName}`))
        .join(', ') : '';

      const formattedAbilityTier = positions_studioemployee.map(position =>
        position.positionId === primaryPositionId ? position.abilityTier : ''
      );

      return {
        profileImage,
        name,
        nickName,
        email,
        employeeType,
        employeeTypeDisplay,
        phoneNumber,
        positions: formattedPositions,
        primaryPosition: primaryPosition
          ? primaryPosition.positionName
          : i18n.t('userProfile:noPosition'),
        origins: formattedOrigins.length ? formattedOrigins : i18n.t('noData'),
        nationalities: formattedNationalities.length
          ? formattedNationalities
          : i18n.t('noData'),
        abilityTier: formattedAbilityTier,
        id,
        isActive,
        isFullTime
      };
    });
  };

  formatPositions = positionData =>
    positionData.map(position => position.positionName);

  getPositions = (user, positionData) => {
    if (user.scopes.includes('employees:read')){
      return this.formatPositions(positionData.data);
    }
    if (user.scopes.includes('studio-models:read')){
      return this.formatPositions(positionData.data).filter(position => modelPositions.includes(position));
    }
    return [];
  }

  fetchAll = () => {
    const { showInactive } = this.state;
    const { user } = this.props;
    Promise
      .all([
        fetchStudioEmployeeBasic(),
        fetchPositionsBasic()
      ])
      .then(
      ([employeeData, positionData]) => {
        this.employees = this.formatEmployees(
          Object.values(employeeData.data)
        );
        const allPositions = this.getPositions(user, positionData);
        const filteredEmployees = showInactive
          ? this.employees
          : this.employees.filter(employee => employee.isActive === true);
        this.setState({
          filteredEmployees,
          allPositions
        });
      })
      .catch(error => {
        this.displaySnackbar(`${i18n.t('fetchError')} ${error.response}`);
      });
  };

  handleShowInactive = () => {
    this.setState(prevState => ({
      showInactive: !prevState.showInactive,
      filteredEmployees: this.filterEmployees(
        !prevState.showInactive,
        prevState.filteredPosition,
        prevState.filteredEmployeeType
      )
    }));
  };

  componentDidMount = () => {
    this.fetchAll();
  };

  handleRowClick = (event, rowData) => {
    const route = `/user-profile/${rowData.id}`;
    const { history } = this.props;

    history.push({
      pathname: route
    });
  };

  handlePositionSelect = event => {
    const filteredPosition = event.target.value;
    this.setState(prevState => ({
      filteredPosition,
      filteredEmployees: this.filterEmployees(
        prevState.showInactive,
        filteredPosition,
        prevState.filteredEmployeeType
      )
    }));
  };

  handleEmployeeTypeSelect = event => {
    const filteredEmployeeType = event.target.value;
    this.setState(prevState => ({
      filteredEmployeeType,
      filteredEmployees: this.filterEmployees(
        prevState.showInactive,
        prevState.filteredPosition,
        filteredEmployeeType
      )
    }));
  };

  showAddEmployeeModal = () => {
    this.addEmployeeModalRef.current.showDialog();
  };

  render() {
    const {
      filteredPosition,
      filteredEmployeeType,
      filteredEmployees,
      allPositions,
      showInactive,
      snackOpen,
      snackbarMessage
    } = this.state;

    const { user } = this.props;

    const positionMenuItems = allPositions.map(position => (
      <MenuItem key={position} value={position}>
        {i18n.t(`positions:${position}`)}
      </MenuItem>
    ));
      
    return (
      <Grid container className="searchView">
        <Grid item xs={4} sm={3} md={2}>
          <Grid container direction="column">
            <Grid item xs={12}>
              <Paper className="role-filter-container">
                <TextField
                  fullWidth
                  select
                  label={i18n.t('filterPosition')}
                  value={filteredPosition}
                  onChange={this.handlePositionSelect}
                  helperText={i18n.t('positionFilterHelp')}
                >
                  <MenuItem value="none">{i18n.t('none')}</MenuItem>
                  {positionMenuItems}
                </TextField>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper className="employeeType-filter-container">
                <TextField
                  fullWidth
                  select
                  label={i18n.t('filterEmployeeType')}
                  value={filteredEmployeeType}
                  onChange={this.handleEmployeeTypeSelect}
                  helperText={i18n.t('employeeTypeFilterHelp')}
                >
                  <MenuItem value="none">{i18n.t('none')}</MenuItem>
                  {employeeTypeMenuItems}
                </TextField>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                className="blackLabel"
                control={
                  <Checkbox
                    checked={showInactive}
                    onChange={this.handleShowInactive}
                  />
                }
                label={i18n.t('showInactive')}
              />
            </Grid>
            <Grid item xs={12}>
              <RequireScope scopes={['employees:write']}>
                <FormControlLabel
                  className="blackLabel"
                  control={
                    <IconButton
                      color="secondary"
                      aria-label="add new employee"
                      onClick={this.showAddEmployeeModal}
                    >
                      <GroupAddIcon />
                    </IconButton>
                  }
                  label={i18n.t('addEmployee')}
                />
              </RequireScope>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={8} sm={9} md={10}>
          <EmployeeSearchTable
            employees={filteredEmployees}
            onRowClick={this.handleRowClick}
          />
        </Grid>
        <Snackbar
          message={snackbarMessage}
          open={snackOpen}
          onClose={this.hideSnackbar}
          autoHideDuration={2000}
        />
        <AddEmployeeModal
          ref={this.addEmployeeModalRef}
          displaySnackbar={this.displaySnackbar}
          refreshEmployees={this.fetchAll}
          user={user}
        />
      </Grid>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
});

SearchView.propTypes = {
  user: UserType.isRequired,
  history: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.object]).isRequired,
}

export default withRouter(connect(mapStateToProps)(SearchView));

export { SearchView as searchView };
